import * as i0 from '@angular/core';
import { Injectable, Input, Directive, EventEmitter, Output, Component, NgModule } from '@angular/core';
import * as i4 from '@angular/router';
import * as i2 from '@yourcause/common/i18n';
import { I18nModule } from '@yourcause/common/i18n';
import * as i3 from '@yourcause/common/notifier';
import { BehaviorSubject, Subject, filter, Subscription, combineLatest, tap, map } from 'rxjs';
import * as i5 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i6 from '@yourcause/common/buttons';
import { ButtonModule } from '@yourcause/common/buttons';
import * as i7 from '@yourcause/common/icons';
import { IconsModule } from '@yourcause/common/icons';
import * as i2$1 from '@yourcause/common/utils';
import { UtilsModule } from '@yourcause/common/utils';

class Tour {
    constructor(steps) {
        this.steps = steps;
        this.start();
    }
    start() {
        this.currentStep = 0;
        this.currentStep$ = new BehaviorSubject(this.currentStep);
        this.complete$ = new BehaviorSubject(false);
        this.renderStep();
    }
    get numberOfSteps() {
        return this.steps.length;
    }
    goToStep(stepNumber) {
        this.removeStep();
        this.currentStep$.next(stepNumber);
        this.currentStep = stepNumber;
        this.renderStep();
    }
    previousStep() {
        this.removeStep();
        this.currentStep$.next(--this.currentStep);
        this.renderStep();
    }
    nextStep() {
        this.removeStep();
        this.currentStep$.next(++this.currentStep);
        this.renderStep();
    }
    complete() {
        this.removeStep();
        this.complete$.next(true);
        this.complete$.complete();
        this.currentStep$.complete();
    }
    removeStep() {
        this.steps[this.currentStep].destroyElement();
    }
    renderStep() {
        if (!this.steps[this.currentStep]) {
            throw new ReferenceError(`Step does not exist: ${this.currentStep}`);
        }
        this.steps[this.currentStep].showStep(this);
    }
}

class TourService {
    constructor() {
        this._finalTourName = 'BASE_TOUR';
        this.tours = {};
        this.numberOfActiveTours = 0;
        this.tourActive$ = new BehaviorSubject(false);
        this.toursCompleted = [];
        this.toursCompleted$ = new Subject();
        this.tours$ = new BehaviorSubject(this.tours);
    }
    get finalTourName() {
        return this._finalTourName;
    }
    get tourActive() {
        return this.numberOfActiveTours !== 0;
    }
    registerTourStep(tourStep) {
        const tour = this.getOrCreateTour(tourStep.ycTour);
        let order = tourStep.ycTourStep;
        if (typeof order !== 'number') {
            order = -1;
            while (tour[order]) {
                --order;
            }
        }
        tour[order] = tourStep;
        this.tours$.next(this.tours);
    }
    deregisterTourStep(tourStep) {
        const tourName = tourStep.ycTour;
        const tour = this.getOrCreateTour(tourName);
        const index = tour.indexOf(tourStep);
        if (index !== -1) {
            tour[index] = undefined;
        }
        this.tours$.next(this.tours);
    }
    initTour(tourName) {
        const tourSteps = this.getOrCreateTour(tourName)
            .filter(step => (step &&
            step.ycTourStepBody &&
            step.ycTourStepLabel));
        const finalTour = this.getOrCreateTour(this.finalTourName);
        if (finalTour.length > 0) {
            const tourLength = tourSteps.length;
            let finalOffset = 0;
            while (!tourSteps[tourLength + finalOffset] && finalTour[finalOffset]) {
                const finalPosition = tourLength + finalOffset;
                tourSteps[finalPosition] = finalTour[finalOffset];
                tourSteps[finalPosition].ycTourStep = finalPosition;
                finalOffset++;
            }
        }
        const tour = new Tour(tourSteps);
        ++this.numberOfActiveTours;
        this.tourActive$.next(true);
        tour.complete$.pipe(filter(complete => complete))
            .subscribe(() => {
            this.toursCompleted$.next(tourName);
            this.toursCompleted.push(tourName);
            --this.numberOfActiveTours;
            this.tourActive$.next(this.tourActive);
        });
        return tour;
    }
    getOrCreateTour(tourName) {
        return this.tours[tourName] = this.tours[tourName] || [];
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: TourService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: TourService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: TourService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

class TourStepDirective {
    constructor(tourService, elementRef) {
        this.tourService = tourService;
        this.elementRef = elementRef;
        this.ycTourXOffset = 0;
        this.ycTourYOffset = 0;
        this.ycTourHaloXOffset = 0;
        this.ycTourHaloYOffset = 0;
        this.ycTourStepPosition = 'bottom';
    }
    ngOnInit() {
        if (this.ycTour) {
            if (typeof (this.ycTourStep) !== 'number') {
                throw new ReferenceError(`Step number not specified for tour: ${this.ycTour}`);
            }
            this.tourService.registerTourStep(this);
        }
    }
    ngOnDestroy() {
        this.tourService.deregisterTourStep(this);
    }
    destroyElement() {
        document.body.removeChild(this.tourStepElement);
        document.body.removeChild(this.haloElement);
        window.removeEventListener('resize', this.listener);
        document.removeEventListener('keyup', this.keyListener);
        clearInterval(this.transitionLoop);
    }
    createHeader(tour) {
        // Header creation
        const headerWrapper = document.createElement('div');
        headerWrapper.className = 'tour-header';
        const header = document.createElement('h4');
        header.innerHTML = this.ycTourStepLabel;
        const exitButton = this.createButton('link', 'close-button');
        exitButton.appendChild(this.createIcon('times-circle'));
        exitButton.onclick = function () {
            tour.complete();
        };
        headerWrapper.appendChild(header);
        headerWrapper.appendChild(exitButton);
        return headerWrapper;
    }
    createHalo(el, width, height, actions) {
        const loopDuration = 2500;
        const element = document.createElement('div');
        const rect = el.getBoundingClientRect();
        // adjusting these just a bit...
        if (actions) {
            element.style.height = `${height}px`;
            element.style.top = `${rect.top + 5 + this.ycTourHaloYOffset}px`;
            element.style.left = `${rect.left + 10 + this.ycTourHaloXOffset}px`;
            element.style.width = `${width}px`;
        }
        else {
            element.style.height = `${height}px`;
            element.style.top = `${rect.top - 5 + this.ycTourHaloYOffset}px`;
            element.style.left = `${rect.left - 5 + this.ycTourHaloXOffset}px`;
            element.style.width = `${width}px`;
        }
        this.haloElement = element;
        this.haloBreath();
        this.transitionLoop = setInterval(() => {
            this.haloBreath();
        }, loopDuration);
        return element;
    }
    haloBreath() {
        this.haloElement.className = 'tour-halo';
        // setTimeout(() => {
        //   this.haloElement.className = 'tour-halo expanded';
        // }, 2250);
    }
    createBody(tour) {
        // Body creation
        const bodyWrapper = document.createElement('div');
        const body = document.createElement('p');
        body.innerHTML = this.ycTourStepBody;
        body.className = 'tour-body';
        bodyWrapper.appendChild(body);
        return bodyWrapper;
    }
    createBackButton(tour) {
        // previous step button
        const backButton = this.createButton('link', 'text-link');
        backButton.appendChild(this.createIcon('chevron-left'));
        backButton.onclick = function () {
            tour.previousStep();
        };
        backButton.disabled = !tour.steps[+tour.currentStep - 1];
        return backButton;
    }
    createStepCircles(tour) {
        // step circle creation
        const stepCircles = document.createElement('div');
        for (let i = 0; i < tour.numberOfSteps; i++) {
            const icon = i === tour.currentStep ? 'circle' : 'circle other-step';
            stepCircles.appendChild(this.createIcon(icon));
        }
        return stepCircles;
    }
    createNextButton(tour) {
        // next step button
        const isLastStep = tour.currentStep === (tour.numberOfSteps - 1);
        const nextButton = this.createButton('link', 'text-link');
        nextButton.appendChild(this.createIcon(!isLastStep ? 'chevron-right' : 'check'));
        nextButton.onclick = function () {
            if (isLastStep) {
                tour.complete();
            }
            else {
                tour.nextStep();
            }
        };
        return nextButton;
    }
    createFooter(tour) {
        // Footer creation
        const footerWrapper = document.createElement('div');
        footerWrapper.className = 'tour-footer';
        footerWrapper.appendChild(this.createBackButton(tour));
        footerWrapper.appendChild(this.createStepCircles(tour));
        footerWrapper.appendChild(this.createNextButton(tour));
        return footerWrapper;
    }
    createElement(tour) {
        // Wrapper creation
        const wrapper = document.createElement('div');
        wrapper.className = `tour-step ${this.ycTourStepPosition}`;
        wrapper.appendChild(this.createHeader(tour));
        wrapper.appendChild(this.createBody(tour));
        wrapper.appendChild(this.createFooter(tour));
        this.tourStepElement = wrapper;
        return wrapper;
    }
    showStep(tour) {
        const element = this.elementRef.nativeElement;
        const width = element.getBoundingClientRect().width + 10;
        const height = element.getBoundingClientRect().height + 10;
        // check for class="action" (table actions column)
        const actions = (element.getElementsByClassName('action')).length ? true : false;
        document.body.appendChild(this.createHalo(element, width, height, actions));
        document.body.appendChild(this.createElement(tour));
        this.applyStyles(element);
        this.listener = () => {
            this.destroyElement();
            this.showStep(tour);
        };
        this.keyListener = (event) => {
            if (event.key === 'ArrowLeft' && (this.ycTourStep !== 0)) {
                tour.previousStep();
            }
            else if (event.key === 'ArrowRight' && (+this.ycTourStep < (tour.numberOfSteps - 1))) {
                tour.nextStep();
            }
            else if (event.key === 'Escape') {
                tour.complete();
            }
        };
        window.addEventListener('resize', this.listener);
        document.addEventListener('keyup', this.keyListener);
    }
    calculateTop(el) {
        const arbitraryPadding = 10;
        const rect = el.getBoundingClientRect();
        const wrapperHeight = rect.height;
        const wrapperTop = rect.top;
        const stepHeight = this.tourStepElement.offsetHeight;
        switch (this.ycTourStepPosition) {
            case 'top': {
                return (wrapperTop - stepHeight - arbitraryPadding) + this.ycTourYOffset;
            }
            case 'bottom': {
                return (wrapperTop + wrapperHeight + arbitraryPadding) + this.ycTourYOffset;
            }
            case 'left': {
                return (wrapperTop + (.5 * wrapperHeight) - (.5 * stepHeight)) + this.ycTourYOffset;
            }
            case 'right': {
                return (wrapperTop + (.5 * wrapperHeight) - (.5 * stepHeight)) + this.ycTourYOffset;
            }
        }
    }
    calculateLeft(el) {
        const arbitraryPadding = 10;
        const rect = el.getBoundingClientRect();
        const wrapperWidth = rect.width;
        const wrapperLeft = rect.left;
        const stepWidth = this.tourStepElement.offsetWidth;
        switch (this.ycTourStepPosition) {
            case 'top': {
                return (wrapperLeft + ((.5 * wrapperWidth) - (.5 * stepWidth))) + this.ycTourXOffset;
            }
            case 'bottom': {
                return (wrapperLeft + (.5 * wrapperWidth) - (.5 * stepWidth)) + this.ycTourXOffset;
            }
            case 'left': {
                return (wrapperLeft - stepWidth - arbitraryPadding) + this.ycTourXOffset;
            }
            case 'right': {
                return (wrapperLeft + wrapperWidth + arbitraryPadding) + this.ycTourXOffset;
            }
        }
    }
    applyStyles(el) {
        if (!el.offsetHeight) {
            el.style.display = 'block';
        }
        this.tourStepElement.style.top = `${this.calculateTop(el)}px`;
        this.tourStepElement.style.left = `${this.calculateLeft(el)}px`;
    }
    createButton(btnName, buttonClass = '') {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'btn btn-' + btnName + (buttonClass ? ' ' + buttonClass : '');
        return button;
    }
    createIcon(iconName) {
        const icon = document.createElement('i');
        icon.className = 'fa fa-' + iconName;
        return icon;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: TourStepDirective, deps: [{ token: TourService }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    /** @nocollapse */ static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.2.10", type: TourStepDirective, isStandalone: false, selector: "[ycTour]", inputs: { ycTour: "ycTour", ycTourStep: "ycTourStep", ycTourStepLabel: "ycTourStepLabel", ycTourStepBody: "ycTourStepBody", ycTourXOffset: "ycTourXOffset", ycTourYOffset: "ycTourYOffset", ycTourHaloXOffset: "ycTourHaloXOffset", ycTourHaloYOffset: "ycTourHaloYOffset", ycTourStepPosition: "ycTourStepPosition" }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: TourStepDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[ycTour]',
                    standalone: false
                }]
        }], ctorParameters: () => [{ type: TourService }, { type: i0.ElementRef }], propDecorators: { ycTour: [{
                type: Input
            }], ycTourStep: [{
                type: Input
            }], ycTourStepLabel: [{
                type: Input
            }], ycTourStepBody: [{
                type: Input
            }], ycTourXOffset: [{
                type: Input
            }], ycTourYOffset: [{
                type: Input
            }], ycTourHaloXOffset: [{
                type: Input
            }], ycTourHaloYOffset: [{
                type: Input
            }], ycTourStepPosition: [{
                type: Input
            }] } });

class RouterTourButtonComponent {
    constructor(tourService, i18n, notifierService, route) {
        this.tourService = tourService;
        this.i18n = i18n;
        this.notifierService = notifierService;
        this.route = route;
        this.defaultTourDisabled = false;
        this.onTourStart = new EventEmitter();
        this.hasRunTour = false;
        this.hasToasterRan = false;
        this.tourButtonDisabled = false;
        this.finalTourName = this.tourService.finalTourName;
        this.viewTourDescription = 'To view this tour again, click <span class="link-color"><i class="fa fa-compass"></i>Tour</span>';
        this.sub = new Subscription();
        this.subscription = combineLatest(route.data, this.tourService.tours$).pipe(tap(_ => this.getTourName()), filter(_ => this.child.data.tourName), map(([, tourStepMap]) => tourStepMap[ /* tourName */this.currentTour]), filter((tourSteps) => !!tourSteps), filter(tourSteps => (tourSteps.length === /* desiredAmount */ this.child.data.tourStepCount) || !this.child.data.tourStepCount))
            .subscribe(_ => this.prepTourForRun());
        this.tourActiveSub = this.tourService.tourActive$.subscribe((active) => {
            this.tourButtonDisabled = active;
        });
    }
    getTourName() {
        let child = this.route.snapshot;
        while (child && child.firstChild) {
            child = child.firstChild;
        }
        this.currentTour = child.data.tourName;
        this.child = child;
    }
    prepTourForRun() {
        // check steps count here and proceed if matches
        const autoRunTour = this.child.queryParamMap.get('runTour');
        if (this.currentTour &&
            !this.tourService.toursCompleted.includes(this.currentTour)) {
            if (!autoRunTour) {
                if (!this.hasToasterRan) {
                    this.hasToasterRan = true;
                    this.notifierService.info(this.i18n.translate('common:notificationTakeATour', {}, 'Take a tour of this page'), undefined, () => this.startTour(true), this.i18n.translate('common:btnTakeTour', {}, 'Take tour'));
                }
                setTimeout(() => {
                    if (!this.hasRunTour) {
                        this.tourService.toursCompleted$.next(this.currentTour);
                    }
                });
            }
            else {
                setTimeout(() => this.startTour());
            }
        }
    }
    startTour(fromToastr = false) {
        this.onTourStart.emit();
        if (fromToastr) {
            this.hasRunTour = true;
        }
        this.tourService.initTour(this.currentTour);
    }
    ngOnDestroy() {
        this.subscription.unsubscribe();
        this.tourActiveSub.unsubscribe();
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: RouterTourButtonComponent, deps: [{ token: TourService }, { token: i2.I18nService }, { token: i3.NotifierService }, { token: i4.ActivatedRoute }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.2.10", type: RouterTourButtonComponent, isStandalone: false, selector: "yc-router-tour-button", inputs: { defaultTourDisabled: "defaultTourDisabled" }, outputs: { onTourStart: "onTourStart" }, ngImport: i0, template: `
    <yc-button
      *ngIf="currentTour"
      size="sm"
      [disabled]="tourButtonDisabled"
      [link]="true"
      [ycTour]="defaultTourDisabled ? '' : finalTourName"
        [ycTourStep]="1"
        [ycTourStepLabel]="'common:hdrViewTour' | ycI18n:'View Tour'"
        [ycTourStepBody]="'common:textViewTourDesription' | ycI18n:viewTourDescription"
        (onClick)="startTour()">
      <yc-fa icon="compass me-1"></yc-fa>
      <yc-i18n key="common:btnTakeTour"></yc-i18n>
    </yc-button>
  `, isInline: true, dependencies: [{ kind: "directive", type: i5.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i6.ButtonComponent, selector: "yc-button", inputs: ["tabIndex", "disabled", "size", "fontSize", "buttonType", "primary", "link", "linkPath", "icon", "secondary", "textDanger", "textWarning", "textPrimary", "textSuccess", "borderOnHover", "noClick", "badgeValue", "value", "ariaDescribedBy", "minWidth", "alignTop", "tooltipText", "popoverTemplate", "container", "popoverContext", "popoverIsOpen", "popoverOutsideClickToClose", "placement", "adaptivePosition", "langKey", "defaultText", "translatedText", "fontAwesomeIcon", "isDropdownButton", "isFooterButton", "dropdownDirection", "dropdownActions", "isDropdownToggle", "isSplitDropdownToggle", "preventClickTracking", "leftAlignLinkText", "analyticsEventName", "customColor", "buttonElementId", "includeExpandedAria", "ariaExpanded"], outputs: ["onClick", "popoverIsOpenChange", "onPopoverReady", "onPopoverClick"] }, { kind: "component", type: i2.I18nComponent, selector: "yc-i18n", inputs: ["key", "defaultValue", "context", "transform"] }, { kind: "component", type: i7.FontAwesomeComponent, selector: "yc-fa", inputs: ["color", "icon", "tooltipText", "popoverTemplate", "light", "regular", "solid"], outputs: ["onClick"] }, { kind: "directive", type: TourStepDirective, selector: "[ycTour]", inputs: ["ycTour", "ycTourStep", "ycTourStepLabel", "ycTourStepBody", "ycTourXOffset", "ycTourYOffset", "ycTourHaloXOffset", "ycTourHaloYOffset", "ycTourStepPosition"] }, { kind: "pipe", type: i2.I18nPipe, name: "ycI18n" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: RouterTourButtonComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'yc-router-tour-button',
                    template: `
    <yc-button
      *ngIf="currentTour"
      size="sm"
      [disabled]="tourButtonDisabled"
      [link]="true"
      [ycTour]="defaultTourDisabled ? '' : finalTourName"
        [ycTourStep]="1"
        [ycTourStepLabel]="'common:hdrViewTour' | ycI18n:'View Tour'"
        [ycTourStepBody]="'common:textViewTourDesription' | ycI18n:viewTourDescription"
        (onClick)="startTour()">
      <yc-fa icon="compass me-1"></yc-fa>
      <yc-i18n key="common:btnTakeTour"></yc-i18n>
    </yc-button>
  `,
                    standalone: false
                }]
        }], ctorParameters: () => [{ type: TourService }, { type: i2.I18nService }, { type: i3.NotifierService }, { type: i4.ActivatedRoute }], propDecorators: { defaultTourDisabled: [{
                type: Input
            }], onTourStart: [{
                type: Output
            }] } });

class TourWrapperComponent {
    constructor(tourService) {
        this.tourService = tourService;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: TourWrapperComponent, deps: [{ token: TourService }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.2.10", type: TourWrapperComponent, isStandalone: false, selector: "yc-tour-wrapper", ngImport: i0, template: `
    <div class="tour-active" [ycHidden]="!tourService.tourActive">
      <div class="mask"></div>
    </div>

    <ng-container>
      <ng-content></ng-content>
    </ng-container>
  `, isInline: true, styles: [".tour-active{position:fixed;inset:0;opacity:1;z-index:999;background-color:transparent;transition:opacity .5s ease-in-out}.tour-active.hidden,.tour-active.hidden .mask{opacity:0;height:0}.tour-active .mask{z-index:99998;height:100%;width:100%;pointer-events:none}\n"], dependencies: [{ kind: "directive", type: i2$1.HiddenDirective, selector: "[ycHidden]", inputs: ["ycHidden"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: TourWrapperComponent, decorators: [{
            type: Component,
            args: [{ selector: 'yc-tour-wrapper', template: `
    <div class="tour-active" [ycHidden]="!tourService.tourActive">
      <div class="mask"></div>
    </div>

    <ng-container>
      <ng-content></ng-content>
    </ng-container>
  `, standalone: false, styles: [".tour-active{position:fixed;inset:0;opacity:1;z-index:999;background-color:transparent;transition:opacity .5s ease-in-out}.tour-active.hidden,.tour-active.hidden .mask{opacity:0;height:0}.tour-active .mask{z-index:99998;height:100%;width:100%;pointer-events:none}\n"] }]
        }], ctorParameters: () => [{ type: TourService }] });

class TourModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: TourModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.2.10", ngImport: i0, type: TourModule, declarations: [TourStepDirective,
            TourWrapperComponent,
            RouterTourButtonComponent], imports: [CommonModule,
            UtilsModule,
            ButtonModule,
            I18nModule,
            IconsModule], exports: [TourStepDirective,
            TourWrapperComponent,
            RouterTourButtonComponent] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: TourModule, imports: [CommonModule,
            UtilsModule,
            ButtonModule,
            I18nModule,
            IconsModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: TourModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        UtilsModule,
                        ButtonModule,
                        I18nModule,
                        IconsModule
                    ],
                    declarations: [
                        TourStepDirective,
                        TourWrapperComponent,
                        RouterTourButtonComponent
                    ],
                    exports: [
                        TourStepDirective,
                        TourWrapperComponent,
                        RouterTourButtonComponent
                    ]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { RouterTourButtonComponent, Tour, TourModule, TourService, TourStepDirective, TourWrapperComponent };
